import { useField } from "formik";
import _ from "lodash";
import { isAddressBlank } from "src/components/Inputs/Address/Book";
import { UPSERT_USER_ADDRESS } from "src/components/Inputs/Address/Book/mutations";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import {
  useClearAnswerForMe,
  usePutAnswerForMe,
} from "src/generated/hooks/form-service/queries";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import {
  AddressBookAnswerSchema,
  AddressFormAnswer,
  FormAddressSchema,
} from "src/schemas/Address";
import { formatNumberStringToNumber } from "src/services/format";
import {
  FormikFieldValue,
  getSingleCustomQuestionAnswerUpsertPayload,
  isAddressAnswer,
  isCustomQuestionAnswer,
  isFreeTextAnswer,
  isMultiSelectAnswer,
  isSingleSelectAnswer,
} from "src/services/formTemplate/answer";
import { validateDate } from "src/services/formTemplate/question";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import {
  DELETE_CUSTOM_QUESTION_ANSWERS,
  DELETE_FORM_ADDRESS,
  INSERT_FORM_ANSWER_OPTIONS,
  UPSERT_CUSTOM_QUESTION_ANSWERS,
  UPSERT_FORM_ADDRESS,
  UPSERT_FORM_ANSWER_DATE,
  UPSERT_FORM_ANSWER_FREE_TEXT,
  UPSERT_FORM_ANSWER_NUMBER,
} from "../graphql/mutations";

export type SingleSelectChangeProps = {
  before?: AF.Option<AF.WithId>;
  after?: AF.Option<AF.WithId>;
};
export type AddressAnswerChangeProps = {
  before?: AddressFormAnswer;
  after?: AddressFormAnswer;
};

export function useFormQuestion(
  formId: uuid,
  question: AF.Question<AF.WithId>
) {
  const flags = useFlags(["form-service-form-answers"]);
  const isFormServiceEnabled = flags["form-service-form-answers"].enabled;

  const [upsertFreeText] = useRemoteDataMutation<
    GQL.UpsertFormAnswerFreeText,
    GQL.UpsertFormAnswerFreeTextVariables
  >(UPSERT_FORM_ANSWER_FREE_TEXT, { fetchPolicy: "no-cache" });

  const [upsertDate] = useRemoteDataMutation<
    GQL.UpsertFormAnswerDate,
    GQL.UpsertFormAnswerDateVariables
  >(UPSERT_FORM_ANSWER_DATE, { fetchPolicy: "no-cache" });

  const [upsertNumber] = useRemoteDataMutation<
    GQL.UpsertFormAnswerNumber,
    GQL.UpsertFormAnswerNumberVariables
  >(UPSERT_FORM_ANSWER_NUMBER, { fetchPolicy: "no-cache" });

  const [insertAnswerOptions] = useRemoteDataMutation<
    GQL.InsertFormAnswerOptions,
    GQL.InsertFormAnswerOptionsVariables
  >(INSERT_FORM_ANSWER_OPTIONS, { fetchPolicy: "no-cache" });

  const [upsertFormAddress] = useRemoteDataMutation<
    GQL.UpsertFormAddress,
    GQL.UpsertFormAddressVariables
  >(UPSERT_FORM_ADDRESS, {
    fetchPolicy: "no-cache",
  });

  const [upsertUserAddress] = useRemoteDataMutation<
    GQL.UpsertUserAddress,
    GQL.UpsertUserAddressVariables
  >(UPSERT_USER_ADDRESS, { fetchPolicy: "no-cache" });

  const [deleteFormAddress] = useRemoteDataMutation<
    GQL.DeleteFormAddress,
    GQL.DeleteFormAddressVariables
  >(DELETE_FORM_ADDRESS, {
    fetchPolicy: "no-cache",
  });

  const [upsertCustomQuestionAnswers] = useRemoteDataMutation<
    GQL.UpsertCustomQuestionAnswers,
    GQL.UpsertCustomQuestionAnswersVariables
  >(UPSERT_CUSTOM_QUESTION_ANSWERS, { fetchPolicy: "no-cache" });

  const [deleteCustomQuestionAnswers] = useRemoteDataMutation<
    GQL.DeleteCustomQuestionAnswers,
    GQL.DeleteCustomQuestionAnswersVariables
  >(DELETE_CUSTOM_QUESTION_ANSWERS, { fetchPolicy: "no-cache" });

  const { mutateAsync: putAnswerForMe } = usePutAnswerForMe(undefined, {
    onSuccess: (data) => {
      if ("error" in data) throw new Error(data.error?.error.message);
    },
  });
  const { mutateAsync: clearAnswerForMe } = useClearAnswerForMe(undefined, {
    onSuccess: (data) => {
      if ("error" in data) throw new Error(data.error?.error.message);
    },
  });

  const [, , helpers] = useField<FormikFieldValue>(question.id);

  return {
    saveFreeTextAnswer: async (
      value: FormikFieldValue,
      validate?: (text: string) => boolean
    ): Promise<boolean> => {
      if (!isFreeTextAnswer(value)) {
        console.error(
          `Expecting a "string" for question ${question.id}, but got ${value} instead`
        );
        return false;
      }

      // skip saving incomplete or invalid text that is expected to be certain format, e.g. email or phone number
      if (value && validate && !validate(value)) return false;

      if (isFormServiceEnabled) {
        if (!value) {
          await clearAnswerForMe({
            path: {
              formId: formId,
              questionId: question.id,
            },
          });
        } else {
          await putAnswerForMe({
            path: {
              formId: formId,
              questionId: question.id,
            },
            body: {
              data: value,
            },
          });
        }
      } else {
        await upsertFreeText({
          variables: {
            form_id: formId,
            question_id: question.id,
            free_text_answer: value,
          },
        });
      }
      return true;
    },
    saveDateAnswer: async (value: FormikFieldValue): Promise<boolean> => {
      const stringDate = value?.toString();

      if (!isFreeTextAnswer(stringDate)) {
        console.error(
          `Expecting a "string" for question ${question.id}, but got ${stringDate} instead`
        );
        return false;
      }

      // skip saving incomplete or invalid date (format / calendar / constraints)
      if (
        stringDate &&
        validateDate(
          stringDate,
          question.type === AF.DateType ? question.constraints : undefined
        )
      ) {
        return false;
      }

      if (isFormServiceEnabled) {
        if (!stringDate) {
          await clearAnswerForMe({
            path: {
              formId: formId,
              questionId: question.id,
            },
          });
        } else {
          await putAnswerForMe({
            path: {
              formId: formId,
              questionId: question.id,
            },
            body: {
              data: stringDate,
            },
          });
        }
      } else {
        await upsertDate({
          variables: {
            form_id: formId,
            question_id: question.id,
            date_answer:
              stringDate && stringDate.length > 0 ? stringDate : null,
          },
        });
      }
      return true;
    },

    saveNumberAnswer: async (value: FormikFieldValue): Promise<boolean> => {
      const number = formatNumberStringToNumber(value);

      if (isFormServiceEnabled) {
        if (!number) {
          await clearAnswerForMe({
            path: {
              formId: formId,
              questionId: question.id,
            },
          });
        } else {
          await putAnswerForMe({
            path: {
              formId: formId,
              questionId: question.id,
            },
            body: {
              data: number,
            },
          });
        }
      } else {
        await upsertNumber({
          variables: {
            form_id: formId,
            question_id: question.id,
            number_answer: number,
          },
        });
      }

      return true;
    },

    saveMultiSelectAnswer: async (
      value: FormikFieldValue
    ): Promise<boolean> => {
      if (!isMultiSelectAnswer(value)) {
        console.error(
          `Expecting a "string[]" for question ${question.id}, but got ${value} instead`
        );
        return false;
      }

      if (isFormServiceEnabled) {
        const options = value?.map((value) => value) ?? [];

        if (options.length === 0) {
          await clearAnswerForMe({
            path: {
              formId: formId,
              questionId: question.id,
            },
          });
        } else {
          await putAnswerForMe({
            path: {
              formId: formId,
              questionId: question.id,
            },
            body: {
              data: options,
            },
          });
        }
      } else {
        await insertAnswerOptions({
          variables: {
            form_id: formId,
            question_id: question.id,
            options:
              value?.map((value) => ({
                form_question_option_id: value,
              })) ?? [],
          },
        });
      }
      return true;
    },

    saveSingleSelectAnswer: async (
      initialValue: FormikFieldValue,
      value: FormikFieldValue,
      confirmEligibilityChange?: (
        value: SingleSelectChangeProps
      ) => Promise<boolean>
    ): Promise<boolean> => {
      if (!isSingleSelectAnswer(value)) {
        console.error(
          `Expecting a "string" for question ${question.id}, but got ${value} instead`
        );
        return false;
      }

      if (question.type !== "SingleSelect") {
        console.error(
          `Expecting SingleSelect type, but got a ${question.type} instead`
        );
        return false;
      }

      if (question.category === "Eligibility") {
        const options = question.options;
        const beforeValue = initialValue;
        const before = options.find((o) => o.id === beforeValue);
        const after = options.find((o) => o.id === value);

        if (
          confirmEligibilityChange &&
          !(await confirmEligibilityChange({ before, after }))
        ) {
          helpers.setValue(beforeValue);
          return false;
        }
      }

      if (isFormServiceEnabled) {
        if (!value) {
          await clearAnswerForMe({
            path: {
              formId: formId,
              questionId: question.id,
            },
          });
        } else {
          await putAnswerForMe({
            path: {
              formId: formId,
              questionId: question.id,
            },
            body: {
              data: value,
            },
          });
        }
      } else {
        await insertAnswerOptions({
          variables: {
            form_id: formId,
            question_id: question.id,
            options: value ? [{ form_question_option_id: value }] : [],
          },
        });
      }
      return true;
    },

    saveAddressAnswer: async (
      initialValue: FormikFieldValue,
      addressValue: FormikFieldValue,
      confirmAddressChange?: (
        value: AddressAnswerChangeProps
      ) => Promise<boolean>
    ): Promise<boolean> => {
      // addressValue may be empty string when answer is initialized from a branching question.
      // Cast empty string value to undefined.
      const value = addressValue === "" ? undefined : addressValue;
      if (value !== undefined && !isAddressAnswer(value)) {
        console.error(
          `Expecting an address answer for question ${
            question.id
          }, but got ${JSON.stringify(value)} instead`
        );
        return false;
      }

      if (question.type !== AF.AddressType) {
        console.error(
          `Expecting Address type, but got a ${question.type} instead`
        );
        return false;
      }

      if (isAddressBlank(value)) {
        if (isFormServiceEnabled) {
          await clearAnswerForMe({
            path: {
              formId: formId,
              questionId: question.id,
            },
          });
        } else {
          await deleteFormAddress({
            variables: {
              form_id: formId,
              question_id: question.id,
            },
          });
        }
        helpers.setTouched(false);
        return false;
      }

      const beforeValue = initialValue;
      if (
        confirmAddressChange &&
        !(await confirmAddressChange({
          before: isAddressAnswer(beforeValue) ? beforeValue : undefined,
          after: value,
        }))
      ) {
        // set touched to false to prevent address form from triggering save on change instead of on blur
        helpers.setTouched(false);

        // address change can't be confirmed, reverting to previous value
        helpers.setValue(beforeValue);
        return false;
      }

      const address = FormAddressSchema.parse(value);
      if (isFormServiceEnabled) {
        await putAnswerForMe({
          path: {
            formId: formId,
            questionId: question.id,
          },
          body: {
            data: address,
          },
        });
      } else {
        await upsertFormAddress({
          variables: {
            address: {
              form_id: formId,
              question_id: question.id,
              ...address,
            },
          },
        });
      }

      const addressBookAddress = AddressBookAnswerSchema.safeParse(value);
      // TODO: need form-service endpoint for managing address book entries
      if (addressBookAddress.success) {
        const {
          user_id,
          street_address,
          street_address_line_2,
          city,
          state,
          zip_code,
          geom,
        } = addressBookAddress.data;
        await upsertUserAddress({
          variables: {
            user_address: {
              user_id,
              street_address,
              street_address_line_2,
              city,
              state,
              zip_code,
              geom,
            },
          },
        });
      }

      // set touched to false to prevent address form from triggering save on change instead of on blur
      helpers.setTouched(false);
      return true;
    },

    /**
     * A question using a custom_question_type with N custom_question_type_fields
     * will have N associated custom_answer records.
     */
    saveCustomQuestionAnswer: async (
      value: FormikFieldValue
    ): Promise<boolean> => {
      if (!isCustomQuestionAnswer(value)) {
        console.error(
          `Expecting an "object" for question ${question.id}, but got ${value} instead`
        );
        return false;
      }
      if (question.type !== AF.CustomQuestionType) {
        console.error(
          `Expecting question ${question.id} to be a custom question type.`
        );
      }
      const customQuestion = question as AF.CustomQuestion<AF.WithId>;

      const answersByQuestionId = value.answersByQuestionId;

      if (_.isEmpty(answersByQuestionId)) {
        if (isFormServiceEnabled) {
          await clearAnswerForMe({
            path: {
              formId: formId,
              questionId: question.id,
            },
          });
        } else {
          await deleteCustomQuestionAnswers({
            variables: {
              form_id: formId,
              question_id: question.id,
              nested_question_ids: customQuestion.nestedQuestions.map(
                (nestedQuestion) => {
                  return nestedQuestion.id;
                }
              ),
            },
          });
        }
        helpers.setTouched(false);
        return false;
      }

      if (isFormServiceEnabled) {
        await putAnswerForMe({
          path: {
            formId: formId,
            questionId: question.id,
          },
          body: {
            data: {
              answersByQuestionId: answersByQuestionId,
              answerBankId: value.answerBankId,
              referenceId: value.referenceId,
              lastUsedAt: value.lastUsedAt,
            },
          },
        });
      } else {
        await upsertCustomQuestionAnswers({
          variables: {
            form_id: formId,
            question_id: customQuestion.id,
            single_select_question_ids: customQuestion.nestedQuestions
              .filter(
                (nestedQuestion) => nestedQuestion.type === AF.SingleSelectType
              )
              .map((nestedQuestion) => nestedQuestion.id),
            custom_question_answers: getSingleCustomQuestionAnswerUpsertPayload(
              formId,
              customQuestion,
              answersByQuestionId
            ),
            no_answer_bank_relationship: value.answerBankId === undefined,
            answer_bank_id: value.answerBankId,
          },
        });
      }
      return true;
    },
  };
}
